import { useRef, useEffect } from "react";
import { useStream } from "@langchain/langgraph-sdk/react";
import type { BagTemplate } from "@langchain/langgraph-sdk";
import { SmartStreamStore } from "./SmartStreamStore";
import {
  getStoreKey,
  getOrCreateStore,
  acquireStore,
  releaseStore,
  rekeyStore,
  registerName,
  markThreadMaterialized,
  isThreadMaterialized,
} from "./StreamRegistry";
import type { useLanggraphStreamOptions, StreamSnapshot } from "./types";
import { dedupeMessagesById, filterContextMessages, filterEmptyMessages, normalizeMessageTypes } from "./messageFilters";
import { useSubmitMiddlewares } from "./SubmitMiddleware";

/**
 * Extract only the visible text from message content for comparison.
 * Content can be a string or an array of content blocks. Tool-use blocks
 * stream token-by-token (file diffs growing) but aren't visible in chat,
 * so we ignore them — only text blocks matter for scroll/re-render decisions.
 */
function visibleContentFingerprint(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .filter((block: any) => block?.type === "text")
    .map((block: any) => block.text ?? "")
    .join("\n");
}

function contentEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return visibleContentFingerprint(a) === visibleContentFingerprint(b);
}

/**
 * Fingerprint of every tool_use content block's input string. During
 * streaming the input grows token-by-token (`{` → `{"op` → … → full
 * JSON). The visible-text fingerprint above ignores tool_use blocks,
 * so a tool-only AIMessage would compare equal across partials and the
 * stable reference would get reused — the ToolCallIndicator (which
 * reads its label off the streaming input string) would then sit on
 * its placeholder for the entire call.
 */
function toolUseInputFingerprint(content: unknown): string {
  if (!Array.isArray(content)) return "";
  return content
    .filter((b: any) => b?.type === "tool_use")
    .map((b: any) => `${b.id ?? ""}:${typeof b.input === "string" ? b.input.length : ""}`)
    .join("|");
}

/**
 * Fast structural comparison of two message arrays.
 * Returns true if both arrays have the same length, same IDs in order,
 * and the last message has the same content (which changes during streaming).
 * Avoids deep comparison of every message on every event.
 */
function messagesStructurallyEqual(prev: any[], next: any[]): boolean {
  if (prev === next) return true;
  if (!prev || !next || prev.length !== next.length) return false;
  if (prev.length === 0) return true;

  // Check last message content (changes during token streaming)
  const lastPrev = prev[prev.length - 1];
  const lastNext = next[next.length - 1];
  if (lastPrev?.id !== lastNext?.id || !contentEqual(lastPrev?.content, lastNext?.content)) return false;

  // Check tool_use input strings — they grow mid-stream on the same
  // message even when visible text doesn't change. The ToolCallIndicator
  // reads its label off these strings, so a stale reference here =
  // indicator stuck on its placeholder for the whole call.
  if (toolUseInputFingerprint(lastPrev?.content) !== toolUseInputFingerprint(lastNext?.content)) {
    return false;
  }

  // Check tool_calls — these appear on AI messages mid-stream before text changes
  const prevToolCalls = lastPrev?.tool_calls?.length ?? 0;
  const nextToolCalls = lastNext?.tool_calls?.length ?? 0;
  if (prevToolCalls !== nextToolCalls) return false;

  // Check all IDs match (covers message additions/removals/reordering)
  for (let i = 0; i < prev.length - 1; i++) {
    if (prev[i]?.id !== next[i]?.id) return false;
  }

  return true;
}

/**
 * Main hook: wraps useStream and syncs the result into a SmartStreamStore.
 *
 * One component should call useLanggraphStream. All other components subscribe
 * to the returned store (or via SmartStreamProvider + useStreamSelector).
 *
 * @example
 * ```tsx
 * function ChatRoot() {
 *   const store = useLanggraphStream({
 *     apiUrl: "http://localhost:2024",
 *     assistantId: "agent",
 *   });
 *   return (
 *     <SmartStreamProvider store={store}>
 *       <MessageList />
 *       <StatusBar />
 *     </SmartStreamProvider>
 *   );
 * }
 * ```
 */
export function useLanggraphStream<
  StateType extends Record<string, unknown> = Record<string, unknown>,
  Bag extends BagTemplate = BagTemplate
>(
  options: useLanggraphStreamOptions<StateType, Bag>
): SmartStreamStore<StateType, Bag> {
  const { name, threadReserved, ...useStreamOptions } = options;

  // Reserved-thread mode: caller has a threadId persisted somewhere (Rails
  // Chat.thread_id, client-minted UUID, etc.) but Langgraph has no checkpoint
  // for it yet. We capture the id, then OMIT the threadId key from inner
  // useStream options — that puts the SDK in *uncontrolled* mode where it
  // tracks threadId internally after creation. (Setting threadId=undefined
  // would put the SDK in *controlled* mode with no thread, forking a fresh
  // thread on every submit — that's the bug uncontrolled mode prevents.)
  // The captured id is forwarded via SubmitOptions.threadId on the first
  // submit so LG creates the thread WITH it. After onThreadId fires we stop
  // forwarding so a follow-up submit (e.g. useStageInit on a pushState
  // substep change, same mount) doesn't try to create the thread again → 409.
  const reservedThreadId = threadReserved ? useStreamOptions.threadId ?? undefined : undefined;
  if (threadReserved) {
    delete useStreamOptions.threadId;
  }
  // Seed from the module-level materialization set so a remount (HMR,
  // error-boundary reset, conditional unmount) doesn't re-arm the deferred
  // create against a thread LG has already seen. Without this seed the fresh
  // ref starts `false`, we re-inject the reserved id on the next submit, and
  // LG's create raises 409. The set survives remounts; this ref doesn't.
  const isMaterializedRef = useRef(
    reservedThreadId ? isThreadMaterialized(reservedThreadId) : false
  );

  // Pull the middleware chain from context. Empty array outside a provider,
  // so callers without one see the same pre-existing behavior.
  const submitMiddlewares = useSubmitMiddlewares();

  // Normalize apiKey: SDK expects string | undefined, not null
  if (useStreamOptions.apiKey === null) {
    useStreamOptions.apiKey = undefined;
  }

  // Track the current threadId for rekey detection
  const threadIdRef = useRef<string | undefined>(options.threadId);
  const keyRef = useRef<string>(
    getStoreKey(
      options.apiUrl ?? "",
      options.assistantId,
      options.threadId ?? undefined
    )
  );

  // Wrap onThreadId to detect threadId changes for rekey
  const originalOnThreadId = useStreamOptions.onThreadId;

  // Cast needed: SDK's InferStateType<StateType> doesn't unify with StateType
  // when generics are still open. This is safe because callers provide concrete types.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stream = useStream({
    // Keep subagent (coder, template-selector) tokens out of the main
    // message stream. The SDK routes any `tools:`-namespaced message — i.e.
    // anything a `task`-spawned subagent produces — into `stream.subagents`
    // instead of `stream.messages`, so the chat shows only the orchestrator's
    // turns. Subagent content stays reachable via `getSubagentsByMessage` for
    // progress cards. Defaulted on; a caller can override through options.
    filterSubagentMessages: true,
    ...useStreamOptions,
    onThreadId: (threadId: string) => {
      // Rekey the store when threadId becomes available
      const oldKey = keyRef.current;
      const newKey = getStoreKey(
        options.apiUrl ?? "",
        options.assistantId,
        threadId
      );

      if (oldKey !== newKey) {
        rekeyStore(oldKey, newKey);
        keyRef.current = newKey;
        threadIdRef.current = threadId;

        // Update name mapping if set
        if (name) {
          registerName(name, newKey);
        }
      }

      // Once the SDK reports a threadId for our deferred id, the thread is
      // live in LG. Subsequent submits must not re-inject our id — record it
      // in the module-level set too so a later remount (which resets this
      // hook's ref) still knows the thread is materialized. See StreamRegistry.
      isMaterializedRef.current = true;
      markThreadMaterialized(threadId);

      originalOnThreadId?.(threadId);
    },
  } as any);

  // Get or create the store
  const key = keyRef.current;
  const store = getOrCreateStore(key, () => {
    return new SmartStreamStore<StateType, Bag>(
      stream as unknown as StreamSnapshot<StateType, Bag>
    );
  });

  // Acquire ref on mount, release on unmount
  const acquiredKeyRef = useRef<string | null>(null);
  if (acquiredKeyRef.current !== key) {
    if (acquiredKeyRef.current !== null) {
      releaseStore(acquiredKeyRef.current);
    }
    acquireStore(key);
    acquiredKeyRef.current = key;
  }

  // Register name if provided
  if (name) {
    registerName(name, key);
  }

  // Sync useStream result to store during render, normalizing and filtering messages.
  // The SDK returns a new messages array reference on every streaming event, even when
  // message content hasn't changed. We memoize by comparing message IDs to preserve
  // referential stability — this prevents downstream effects (scroll anchor, re-renders)
  // from firing on every event.
  const snapshot = stream as unknown as StreamSnapshot<StateType, Bag>;
  const stableMessagesRef = useRef<typeof snapshot.messages>(snapshot.messages);

  // Filter pipeline (innermost first):
  //   1. filterContextMessages — drops `name: "context"` messages.
  //   2. filterEmptyMessages — drops content-less assistant turns.
  //   3. dedupeMessagesById — collapses duplicate-id copies (subgraph
  //      streaming can deliver the same message under two scopes).
  //   4. normalizeMessageTypes — final type cleanup for renderers.
  // Subagent messages are already excluded upstream by the SDK's
  // `filterSubagentMessages` option (see the useStream call above), so
  // `snapshot.messages` only ever holds the orchestrator's turns here.
  const rawNormalized = normalizeMessageTypes(
    dedupeMessagesById(
      filterEmptyMessages(
        filterContextMessages(snapshot.messages)
      )
    )
  );
  const isEqual = messagesStructurallyEqual(stableMessagesRef.current, rawNormalized);
  const normalized = isEqual ? stableMessagesRef.current : rawNormalized;

  stableMessagesRef.current = normalized;

  // Wrap submit to:
  // 1. Run any registered SubmitMiddleware so cross-cutting concerns
  //    (dev-panel scenario replay, e2e scaffolding, request decoration) can
  //    transform values/options without polluting application code. They run
  //    BEFORE the wrap below so optimistic updates and threadId injection
  //    observe their changes.
  // 2. Always enable subgraph streaming so intermediate state updates
  //    (agentIntents, creditStatus, etc.) arrive mid-run, not just at completion.
  // 3. Optimistically append the human message so it renders immediately,
  //    not after the server echoes it back in the final values event.
  // 4. Route `multitaskStrategy: "inbox"` to the agent-inbox endpoint
  //    instead of creating a new run. The running run drains the inbox
  //    at its next beforeModel boundary; user perceives a mid-run
  //    double-text landing as a regular user turn. See plans/agent-inbox.md.
  const originalSubmit = snapshot.submit;
  const wrappedSubmit: typeof originalSubmit = async (values, submitOptions) => {
    // Run user-registered middlewares first. Each sees the previous one's
    // result, so order matters. Empty chain = pure pass-through.
    let mwValues: unknown = values;
    let mwOptions: Record<string, unknown> | undefined = submitOptions;
    for (const middleware of submitMiddlewares) {
      const result = middleware(mwValues, mwOptions);
      mwValues = result.values;
      mwOptions = result.options;
    }
    const transformedValues = mwValues as typeof values;
    const transformedOptions = mwOptions;

    // Mid-run inbox path: skip the SDK entirely. Posts directly to
    // POST /threads/:thread_id/inbox; the drain middleware on the
    // running agent picks it up at the next beforeModel boundary.
    //
    // Auto-route: when a stream is in flight and the caller didn't
    // pick a strategy, default to "inbox" so a submit-during-stream
    // lands on the running run instead of cancelling it (the SDK's
    // legacy default was "interrupt"). Callers who want the old
    // pre-empt behavior can pass `multitaskStrategy: "interrupt"`
    // explicitly. See plans/agent-inbox.md.
    const explicitStrategy = transformedOptions?.multitaskStrategy;
    const shouldUseInbox =
      explicitStrategy === "inbox" ||
      (explicitStrategy === undefined && snapshot.isLoading);
    if (shouldUseInbox) {
      const tid =
        (transformedOptions?.threadId as string | undefined) ??
        threadIdRef.current ??
        reservedThreadId;
      if (!tid) {
        throw new Error(
          "submit({ multitaskStrategy: 'inbox' }) requires an active thread_id; " +
            "either pass options.threadId, set useStreamOptions.threadId, or use threadReserved mode."
        );
      }
      const messages = (transformedValues as Record<string, unknown>)
        ?.messages as Array<{ content?: unknown }> | undefined;
      const first = messages?.[0];
      if (!first || first.content === undefined) {
        throw new Error(
          "submit({ multitaskStrategy: 'inbox' }) requires values.messages[0] with content."
        );
      }
      const kind = (transformedOptions?.kind as string) ?? "user_message";

      // Optimistic queue entry: rendered immediately by consumers via
      // `useChatQueuedMessages`. Skip queueing for non-user kinds
      // (system_event, etc.) — those don't represent user input.
      const localId = `queued-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const enqueued =
        kind === "user_message"
          ? store.addQueuedMessage({
              id: localId,
              // Pass content through verbatim — string OR content-block array
              // (text + image_url + document blocks). The renderer reads it
              // via `<Chat.UserMessage content={entry.content} />`, which
              // already handles every shape `MessageContent` can take. No
              // stringify, no shape narrowing.
              content: first.content as string | Array<Record<string, unknown>>,
              sentAt: Date.now(),
              status: "delivering",
            })
          : null;

      try {
        const res = await postInbox({
          apiUrl: options.apiUrl ?? "",
          apiKey: useStreamOptions.apiKey,
          defaultHeaders: useStreamOptions.defaultHeaders as
            | Record<string, string>
            | undefined,
          threadId: tid,
          body: {
            kind,
            content: first.content,
            ...(typeof transformedOptions?.priority === "number" && {
              priority: transformedOptions.priority,
            }),
            ...(typeof transformedOptions?.source === "string" && {
              source: transformedOptions.source,
            }),
          },
        });

        if (enqueued) {
          store.updateQueuedMessage(enqueued.id, {
            status: "queued",
            inboxRowId: res.id,
          });
        }
      } catch (err) {
        if (enqueued) {
          // Mark failed so the consumer can render a retry affordance
          // instead of silently dropping. The consumer decides whether
          // to leave it visible or auto-remove after a delay.
          store.updateQueuedMessage(enqueued.id, {
            status: "failed",
            error: err instanceof Error ? err.message : String(err),
          });
        }
        throw err;
      }
      return;
    }

    // Extract messages from the (possibly transformed) input to show optimistically
    const inputMessages = (transformedValues as Record<string, unknown>)?.messages as Array<Record<string, unknown>> | undefined;

    const optimisticValues = inputMessages?.length
      ? (prev: StateType) => {
          const prevMessages = (prev as Record<string, unknown>).messages as unknown[] ?? [];
          const optimisticMessages = inputMessages.map((msg) => ({
            id: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type: msg.role === "human" || msg.role === "user" ? "human" : String(msg.role ?? "human"),
            content: msg.content,
            ...(msg.additional_kwargs ? { additional_kwargs: msg.additional_kwargs as Record<string, unknown> } : {}),
          }));
          return { messages: [...prevMessages, ...optimisticMessages] } as unknown as Partial<StateType>;
        }
      : undefined;

    return originalSubmit(transformedValues, {
      ...transformedOptions,
      streamSubgraphs: true,
      multitaskStrategy: transformedOptions?.multitaskStrategy ?? "interrupt",
      ...(optimisticValues && !transformedOptions?.optimisticValues && { optimisticValues }),
      ...(reservedThreadId && !isMaterializedRef.current && !transformedOptions?.threadId && { threadId: reservedThreadId }),
    });
  };

  const filtered = Object.create(snapshot, {
    messages: { value: normalized, enumerable: true },
    submit: { value: wrappedSubmit, enumerable: true },
    // Readable aliases for SDK properties
    isStreaming: { get: () => snapshot.isLoading, enumerable: true },
    isLoadingHistory: { get: () => snapshot.isThreadLoading, enumerable: true },
  });
  store.setSnapshot(filtered);

  // Inbox queue reconciliation: any SDK message carrying
  // `additional_kwargs.inboxRowId` is a server-emitted real message
  // that materialized one of our locally-queued optimistic entries.
  // Drop matching queued entries so the renderer stops showing the
  // "delivering" placeholder once the real bubble lands. Walks
  // normalized messages on every render — cheap (small N), idempotent
  // (no-op when nothing matches).
  const drainedRowIds = collectInboxRowIds(normalized);
  if (drainedRowIds.size > 0) {
    store.removeQueuedMessagesByInboxRowIds(drainedRowIds);
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (acquiredKeyRef.current !== null) {
        releaseStore(acquiredKeyRef.current);
        acquiredKeyRef.current = null;
      }
    };
  }, []);

  // Orphan-flush: handles the inbox race where a row lands between the
  // agent's final drain and the stream finalizing client-side.
  //
  // Lifecycle:
  //   - User submits during isLoading=true → row goes to agent_inboxes,
  //     local queue gets a "queued" entry.
  //   - The middleware drain claims the row → emits a HumanMessage with
  //     `additional_kwargs.inboxRowId` → snapshot-sync above removes
  //     the matching queued entry.
  //   - But: row arrives between final afterAgent (already drained
  //     empty) and the stream closing → row sits, queued entry never
  //     reconciles.
  //
  // This effect watches isLoading transitions. When the stream ends
  // (true → false) and the local queue still holds entries with
  // `status === "queued"` (POST resolved, server has the row), it
  // calls /threads/:id/inbox/flush. The endpoint atomically deletes
  // rows and returns them as messages; we hand them to `submit()` so
  // a fresh run picks them up. The reconcile path then drops the
  // queued entries naturally as the messages echo back.
  //
  // Defensive: a false-positive orphan (queued entry that was actually
  // drained but reconciliation happened a tick after stream close) is
  // harmless — /flush returns `drained: 0` and we no-op.
  const wasLoadingRef = useRef(snapshot.isLoading);
  const flushInFlightRef = useRef(false);
  useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    const isLoading = snapshot.isLoading;
    wasLoadingRef.current = isLoading;

    // Only react to the streaming-ended transition.
    if (!(wasLoading && !isLoading)) return;
    if (flushInFlightRef.current) return;

    const tid = threadIdRef.current ?? reservedThreadId;
    if (!tid) return;

    const orphans = store
      .getQueuedMessages()
      .filter((q) => q.status === "queued" && typeof q.inboxRowId === "number");
    if (orphans.length === 0) return;

    flushInFlightRef.current = true;
    (async () => {
      try {
        const res = await postInboxFlush({
          apiUrl: options.apiUrl ?? "",
          apiKey: useStreamOptions.apiKey,
          defaultHeaders: useStreamOptions.defaultHeaders as
            | Record<string, string>
            | undefined,
          threadId: tid,
        });

        if (res.drained === 0 || res.messages.length === 0) {
          // Race lost — middleware drained these before we got here.
          // The reconcile path handles cleanup as the messages flow
          // through state.messages. Nothing to do.
          return;
        }

        // Hand the drained messages to a fresh run via the SDK's normal
        // submit path. `multitaskStrategy: "interrupt"` is safe: the
        // previous run is already terminated, so nothing to interrupt;
        // it's the most defensive choice if we got here mid-transition.
        await stream.submit(
          { messages: res.messages } as unknown as Parameters<
            typeof stream.submit
          >[0],
          { multitaskStrategy: "interrupt" }
        );
      } catch {
        // Don't surface — this is a recovery path and the user's local
        // queue still shows their message. They can manually retry by
        // submitting again, which goes through the same flush flow.
      } finally {
        flushInFlightRef.current = false;
      }
    })();
  }, [snapshot.isLoading]);

  return store;
}

/**
 * Walk a message array and collect every numeric `inboxRowId` carried
 * on `additional_kwargs`. Used by the snapshot-sync to drop matching
 * queued entries from the local store.
 */
function collectInboxRowIds(messages: ReadonlyArray<unknown>): Set<number> {
  const ids = new Set<number>();
  for (const msg of messages) {
    const ak = (msg as { additional_kwargs?: Record<string, unknown> })
      ?.additional_kwargs;
    const id = ak?.inboxRowId;
    if (typeof id === "number" && Number.isFinite(id)) ids.add(id);
  }
  return ids;
}

/**
 * POST a message to the agent inbox endpoint on langgraph-server.
 *
 * Exported for tests; not part of the public package surface. Uses the
 * same auth surface (`apiKey` / `defaultHeaders`) the SDK uses, so the
 * caller's existing client config carries through.
 *
 * Throws on non-2xx responses with the response body inlined for
 * debugging — caller decides whether to retry, surface to the user,
 * or roll back optimistic UI.
 */
export async function postInbox(args: {
  apiUrl: string;
  apiKey?: string | null;
  defaultHeaders?: Record<string, string>;
  threadId: string;
  body: {
    kind?: string;
    content: unknown;
    priority?: number;
    source?: string;
  };
}): Promise<{ id: number; sent_at: string; kind: string; priority: number }> {
  const url = `${args.apiUrl.replace(/\/$/, "")}/threads/${args.threadId}/inbox`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(args.defaultHeaders ?? {}),
  };
  if (args.apiKey) {
    // Match SDK auth convention. The langgraph-sdk client sends the
    // apiKey as `x-api-key`; mirroring that keeps any custom auth
    // middleware on the server-side path identical.
    headers["x-api-key"] = args.apiKey;
  }

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(args.body),
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`postInbox ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

/**
 * Shape of a message returned by the flush endpoint. Mirrors what the
 * SDK accepts on `submit({ messages })`. Carries the same reconciliation
 * handle (`additional_kwargs.inboxRowId`) the middleware drain emits
 * so the queued-entry cleanup is identical regardless of which path
 * delivered the message.
 */
export interface FlushedMessage {
  id: string;
  role: "human";
  content: unknown;
  additional_kwargs: {
    inboxRowId: number;
    inboxSource: string | null;
  };
}

/**
 * POST `/threads/:id/inbox/flush` — atomically drains pending inbox
 * rows for the thread and returns them as messages ready for
 * `submit()`. Returns `{ drained: 0, messages: [] }` when nothing is
 * pending (the common no-op case — the middleware already handled
 * everything, or the local queue had a false-positive orphan).
 *
 * Exported for tests and for callers that want explicit drain control
 * (e.g. an admin "force flush" debug button). Most callers don't need
 * this directly — the orphan-detection effect inside `useLanggraphStream`
 * calls it automatically at stream-end when the local queue still
 * holds entries.
 */
export async function postInboxFlush(args: {
  apiUrl: string;
  apiKey?: string | null;
  defaultHeaders?: Record<string, string>;
  threadId: string;
}): Promise<{ drained: number; messages: FlushedMessage[] }> {
  const url = `${args.apiUrl.replace(/\/$/, "")}/threads/${args.threadId}/inbox/flush`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(args.defaultHeaders ?? {}),
  };
  if (args.apiKey) headers["x-api-key"] = args.apiKey;

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify({}),
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `postInboxFlush ${res.status}: ${text || res.statusText}`
    );
  }
  return res.json();
}
